import { InputAdornment, TextFieldProps } from '@mui/material';
import { getRuleValue, TextFieldControl as SharedTextFieldControl } from 'probonio-shared-ui/component/form';
import { ReactNode, useMemo } from 'react';
import { FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const createInputAdornmentSlotProps = (
  adornment: ReactNode,
  position: 'start' | 'end' = 'start',
): NonNullable<TextFieldProps['slotProps']> => ({
  input: {
    ...(position === 'start'
      ? { startAdornment: <InputAdornment position="start">{adornment}</InputAdornment> }
      : { endAdornment: <InputAdornment position="end">{adornment}</InputAdornment> }),
    // Dim the adornment to match the disabled input text (InputAdornment keeps text.secondary otherwise).
    sx: { '&.Mui-disabled .MuiInputAdornment-root .MuiTypography-root': { color: 'text.disabled' } },
  },
});

export type TextFieldControlProps<T extends FieldValues, TName extends FieldPath<T>> = UseControllerProps<T, TName> & TextFieldProps;

export const TextFieldControl = <T extends FieldValues, TName extends FieldPath<T>>({
  name,
  rules,
  control,
  defaultValue,
  shouldUnregister,
  ...props
}: TextFieldControlProps<T, TName>): React.JSX.Element => {
  const { t } = useTranslation('errorModule', { keyPrefix: 'form' });

  const resolveHelperText: NonNullable<Parameters<typeof SharedTextFieldControl<T, TName>>[0]['helperTextResolver']> = ({
    fieldState,
    rules,
  }) => {
    const message = fieldState?.error?.message;
    if (fieldState.error?.type === 'min') {
      return message || t('min', { value: getRuleValue(rules?.min) });
    }
    if (fieldState.error?.type === 'max') {
      return message || t('max', { value: getRuleValue(rules?.max) });
    }
    if (fieldState.error?.type === 'pattern') {
      return message || t('pattern');
    }
    if (fieldState.error?.type === 'positive') {
      return message || t('positive');
    }
    if (fieldState.error?.type === 'required') {
      return message || t('required');
    }
    return message || props.helperText;
  };

  const htmlInputProps = useMemo(() => {
    return {
      maxLength: getRuleValue(rules?.maxLength),
      min: getRuleValue(rules?.min),
      max: getRuleValue(rules?.max),
      ...props.slotProps?.htmlInput,
    };
  }, [props.slotProps?.htmlInput, rules?.maxLength, rules?.min, rules?.max]);

  return (
    <SharedTextFieldControl
      {...props}
      name={name}
      rules={rules}
      control={control}
      defaultValue={defaultValue}
      shouldUnregister={shouldUnregister}
      helperTextResolver={resolveHelperText}
      slotProps={{
        ...props.slotProps,
        inputLabel: {
          shrink: true,
          ...props.slotProps?.inputLabel,
        },
        htmlInput: htmlInputProps,
      }}
    />
  );
};
